import React, { useState, useEffect } from 'react';
import { Mail, Download, RefreshCw, Globe, Users, MapPin, MessageSquare } from 'lucide-react';
import { getAllBetaSignups } from '../firebase/betaSignups';

const AdminBetaPage = () => {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSignups();
  }, []);

  const loadSignups = async () => {
    setLoading(true);
    try {
      const data = await getAllBetaSignups();
      setSignups(data);
    } catch (error) {
      console.error('Error loading signups:', error);
    }
    setLoading(false);
  };

  const downloadCSV = () => {
    const headers = ['Email', 'Status', 'Primary Goals', 'Travel Willingness', 'Languages', 'Preferred Concepts', 'Comments', 'Email Submitted', 'Questionnaire Completed'];
    const rows = signups.map(signup => [
      signup.email,
      signup.questionnaireCompleted ? 'Complete' : 'Email Only',
      signup.primaryGoals?.join(', ') || 'N/A',
      signup.travelWillingness?.join(', ') || 'N/A',
      signup.languages?.join(', ') || 'N/A',
      signup.preferredConcepts?.join(', ') || 'N/A',
      signup.comments || 'N/A',
      signup.emailSubmittedAt?.toDate?.().toLocaleString() || signup.submittedAt?.toDate?.().toLocaleString() || 'N/A',
      signup.questionnaireCompletedAt?.toDate?.().toLocaleString() || 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `swissmeet-beta-signups-${new Date().toISOString()}.csv`;
    a.click();
  };

  const getGoalIcon = (goalId) => {
    const icons = {
      events: MapPin,
      meetPeople: Users,
      explore: Globe,
      networking: MessageSquare
    };
    return icons[goalId] || Users;
  };

  const getGoalLabel = (goalId) => {
    const labels = {
      events: 'Events',
      meetPeople: 'Meet People',
      explore: 'Explore',
      networking: 'Networking'
    };
    return labels[goalId] || goalId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-red-600 mx-auto mb-3" />
          <p className="text-gray-600">Loading signups...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Beta Signups</h2>
          <p className="text-gray-600 mt-1">Total signups: {signups.length}</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadSignups}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={downloadCSV}
            disabled={signups.length === 0}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {signups.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-2">
              <Mail className="w-6 h-6 text-blue-600" />
              <span className="text-gray-600">Unique Emails</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {new Set(signups.map(s => s.email)).size}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-2">
              <Users className="w-6 h-6 text-green-600" />
              <span className="text-gray-600">Want to Meet People</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {signups.filter(s => s.primaryGoals?.includes('meetPeople')).length}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-2">
              <Globe className="w-6 h-6 text-red-600" />
              <span className="text-gray-600">Languages</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {new Set(signups.flatMap(s => s.languages || [])).size}
            </p>
          </div>
        </div>
      )}

      {/* Signups Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {signups.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No beta signups yet</h3>
            <p className="text-gray-600">Beta signups will appear here when users submit the form</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Primary Goals
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Travel
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Languages
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Concepts
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Comments
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {signups.map((signup, index) => (
                  <tr key={signup.id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Mail className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">{signup.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        signup.questionnaireCompleted 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {signup.questionnaireCompleted ? '✓ Complete' : 'Email Only'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {signup.primaryGoals?.map(goal => {
                          const Icon = getGoalIcon(goal);
                          return (
                            <span
                              key={goal}
                              className="inline-flex items-center space-x-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs"
                            >
                              <Icon className="w-3 h-3" />
                              <span>{getGoalLabel(goal)}</span>
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {signup.travelWillingness?.map(travel => (
                          <span
                            key={travel}
                            className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs"
                          >
                            {travel}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {signup.languages?.map(lang => (
                          <span
                            key={lang}
                            className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs"
                          >
                            {lang}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {signup.preferredConcepts?.map(concept => (
                          <span
                            key={concept}
                            className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs"
                          >
                            {concept === 'matching' ? 'User Matching' : concept === 'directory' ? 'Community Directory' : 'Events Platform'}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <p className="text-sm text-gray-600 truncate" title={signup.comments}>
                        {signup.comments || '-'}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {signup.submittedAt?.toDate?.().toLocaleDateString() || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBetaPage;
